/**
 * Zonas 1 e 2 da página do setor: cabeçalho compacto (voltar ao mapa, nome
 * do setor, a etapa da planilha a que ele está ligado, de onde vêm os dados e
 * quem está apontando) e a faixa de KPIs, cada um levando ao recorte que ele
 * conta.
 */
import React from "react";
import { FiArrowLeft, FiPlus, FiSettings } from "react-icons/fi";

import KpiStrip from "../../../components/KpiStrip";
import { plural } from "../../../utils/dinheiro";
import { ehMacro } from "../dados/etapasPlanilha";
import { rotuloDaEtapa } from "../setores";
import { BotoesCabecalho, Zona1 } from "./styles";

const dois = (n) => String(n).padStart(2, "0");

export function origemDaPlanilha(meta) {
  const quando = meta?.timestamp ? new Date(meta.timestamp) : null;
  if (!quando || Number.isNaN(quando.getTime())) return "Dados da planilha";
  return (
    `Planilha carregada em ${dois(quando.getDate())}/${dois(quando.getMonth() + 1)} ` +
    `${dois(quando.getHours())}:${dois(quando.getMinutes())}`
  );
}

/**
 * De onde este setor recebe trabalho, em texto. O nome do setor é do gestor;
 * a etapa é da planilha — quem não diz qual é qual deixa o gestor sem saber
 * por que um ambiente apareceu aqui.
 */
export function vinculoDoSetor(setor) {
  const etapa = rotuloDaEtapa(setor?.etapa);
  if (!etapa) return "Sem etapa da planilha ligada";
  // Grupo inteiro não é "a etapa X": o rótulo já vem como "todo o grupo X".
  const grupo = ehMacro(setor?.etapa);
  if (setor?.recebe_da_planilha) return grupo ? `Recebe ${etapa} da planilha` : `Recebe a etapa ${etapa} da planilha`;
  return grupo ? `Liga-se a ${etapa} · quem recebe é outro setor` : `Etapa ${etapa} · quem recebe é outro setor`;
}

/**
 * Itens do KpiStrip. `acoes` = {verEntidade(entidade), filtrar(id, valor),
 * configurarColaboradores?} — sem `configurarColaboradores`, o KPI de
 * colaboradores só informa.
 */
export function montarKpis(resumo, { noFiltro, filtrando }, acoes) {
  // A linha de baixo de cada KPI diz o que o NÚMERO é; o que o toque faz vai
  // no `title`. Misturar os dois ("toque para filtrar" ao lado de "prazo que
  // vale vencido") deixava a faixa sem uma leitura só.
  return [
    {
      key: "ambientes",
      label: "Ambientes",
      value: resumo.ambientes,
      sub: filtrando ? `${noFiltro} no filtro` : "no setor pela planilha",
      title: "Ver a lista de ambientes",
      onClick: () => acoes.verEntidade("ambiente"),
    },
    {
      key: "producao",
      label: "Em produção",
      value: resumo.emProducao,
      sub: resumo.emProducao ? "apontados por alguém" : "ninguém apontou ainda",
      title: "Filtrar a tela pelos ambientes em produção",
      onClick: () => acoes.filtrar("statusAmbiente", ["em_producao"]),
    },
    {
      key: "pausados",
      label: "Pausados",
      value: resumo.pausados,
      sub: resumo.pausados ? "parados com motivo" : "nenhum parado",
      title: "Filtrar a tela pelos ambientes pausados",
      onClick: () => acoes.filtrar("statusAmbiente", ["pausado"]),
    },
    {
      key: "atrasados",
      label: "Atrasados",
      value: resumo.atrasados,
      sub: resumo.atrasados ? "prazo que vale vencido" : "nenhum atraso",
      title: "Filtrar a tela pelos ambientes atrasados",
      onClick: () => acoes.filtrar("atrasadas", "sim"),
    },
    {
      key: "tarefas",
      label: "Tarefas abertas",
      value: resumo.tarefasAbertas,
      sub: resumo.tarefasAtrasadas ? plural(resumo.tarefasAtrasadas, "atrasada", "atrasadas") : "nenhuma atrasada",
      title: "Ver a lista de tarefas",
      onClick: () => acoes.verEntidade("tarefa"),
    },
    {
      key: "pecas",
      label: "Peças fora",
      value: resumo.pecasFora,
      sub: resumo.pecasAtrasadas ? plural(resumo.pecasAtrasadas, "atrasada", "atrasadas") : "desmembradas dos ambientes",
      title: "Ver a lista de peças desmembradas",
      onClick: () => acoes.verEntidade("peca"),
    },
    {
      key: "colaboradores",
      label: "Colaboradores",
      value: resumo.colaboradores,
      sub: resumo.colaboradores ? "vinculados a este setor" : "ninguém cadastrado",
      title: acoes.configurarColaboradores ? "Cadastrar e vincular colaboradores" : undefined,
      onClick: acoes.configurarColaboradores,
    },
  ];
}

/** Props: setor, resumo, meta, nomeUsuario, kpis, onVoltar, onConfigurar?, onNovaTarefa?. */
export default function CabecalhoDoSetor({ setor, resumo, meta, nomeUsuario, kpis, onVoltar, onConfigurar, onNovaTarefa }) {
  return (
    <Zona1>
      <header className="eh">
        <button type="button" className="eh__back" onClick={onVoltar}>
          <FiArrowLeft size={14} aria-hidden="true" /> Mapa
        </button>
        <div className="eh__id">
          <div className="eh__id-top">
            <h1 className="eh__title">{setor.nome}</h1>
            <span className={`badge ${resumo.atrasados ? "danger" : "neutral"}`}>
              {resumo.atrasados ? plural(resumo.atrasados, "atrasado", "atrasados") : "Sem atrasos"}
            </span>
          </div>
          <div className="eh__sub">
            {vinculoDoSetor(setor)} · {origemDaPlanilha(meta)} · Você está como {nomeUsuario}
          </div>
        </div>
        <BotoesCabecalho>
          {onConfigurar && (
            <button type="button" className="btn btn--secondary" onClick={onConfigurar}>
              <FiSettings size={14} aria-hidden="true" /> Configurar setor
            </button>
          )}
          {onNovaTarefa && (
            <button type="button" className="btn btn--primary" onClick={onNovaTarefa}>
              <FiPlus size={14} aria-hidden="true" /> Nova tarefa
            </button>
          )}
        </BotoesCabecalho>
      </header>
      <KpiStrip items={kpis} standalone={false} />
    </Zona1>
  );
}
